import { MedicalTitleName, UserStatus } from '@caresend/types';
import update from 'immutability-helper';
import { getNonEmptyStringErrors, getNumberErrors, getBooleanErrors } from './helpers.js';
import { getKeyValidationErrors } from './validation.js';

const isOrderedMedicalTitle = title => [MedicalTitleName.RN, MedicalTitleName.LVN, MedicalTitleName.PHLEBOTOMIST].includes(title);
const requiredInsuranceInfoKeys = {
  carrier: true,
  carrierID: true,
  number: true,
  planType: true,
  primary: true
};
/**
 * Determine whether an object of type Partial<InsuranceInfo> meets
 * all requirements to be non-partial
 */

const isInsuranceInfoValid = info => !!info && !getKeyValidationErrors(info, requiredInsuranceInfoKeys);
/**
 * Evaluate an object of type Partial<InsuranceInfo> with all
 * required properties to resolve a non-partial type
 */

const getValidatedInsuranceInfo = info => {
  if (!isInsuranceInfoValid(info)) {
    throw Error('InsuranceInfo is missing required properties');
  }

  return info;
};
const requiredPhoneNumberKeys = {
  callingCode: true,
  countryCode: true,
  number: true
};
/**
 * Determine whether an object of type Partial<PhoneNumber> meets
 * all requirements to be non-partial
 */

const getPhoneNumberErrors = phoneNumber => {
  if (!phoneNumber) return null;
  return getKeyValidationErrors(phoneNumber, requiredPhoneNumberKeys);
};
const isPhoneNumber = phoneNumber => !getPhoneNumberErrors(phoneNumber);
/**
 * Evaluate an object of type Partial<PhoneNumber> with all
 * required properties to resolve a non-partial type
 */

const getValidatedPhoneNumber = phoneNumber => {
  if (!isPhoneNumber(phoneNumber)) {
    throw Error('Phone number is missing required properties');
  }

  return phoneNumber;
};
const requiredUserInfoKeys = {
  firstName: getNonEmptyStringErrors,
  lastName: getNonEmptyStringErrors,

  /**
   * Patients can be saved with `email: ''`. For these patients,
   * `<randomID>@caresend.com` is used when creating the user in firebase auth.
   */
  email: true,
  phone: getPhoneNumberErrors
};
const getUserInfoErrors = info => {
  if (!info) return 'Expected user info';
  return getKeyValidationErrors(info, requiredUserInfoKeys);
};
/**
 * Determine whether an object of type Partial<EncryptedUser['info']> meets
 * all requirements to be non-partial
 */

const isUserInfo = info => !getUserInfoErrors(info);
/**
 * Evaluate an object of type Partial<InfoUser> with all
 * required properties to resolve a non-partial type
 */

const getValidatedUserInfo = info => {
  if (!isUserInfo(info)) {
    throw Error('User info is missing required properties');
  }

  return info;
};
const requiredUserKeys = {
  creationTimestamp: getNumberErrors,
  id: getNonEmptyStringErrors,
  info: getUserInfoErrors,
  passwordCreated: getBooleanErrors,
  role: true,
  status: true
};
/**
 * Determine whether an object of type Partial<EncryptedUser> meets
 * all requirements to be non-partial
 */

const isUserValid = user => !getKeyValidationErrors(user, requiredUserKeys);

class UserCleaner {
  constructor(user) {
    this._user = user;
  }

  get user() {
    return this._user;
  }

  cleanUpPhone() {
    var _a, _b;

    if (!((_b = (_a = this._user.info) === null || _a === void 0 ? void 0 : _a.phone) === null || _b === void 0 ? void 0 : _b.number)) {
      this._user = update(this._user, {
        info: {
          $unset: ['phone']
        }
      });
    }

    return this;
  }

}

const cleanUpUser = user => new UserCleaner(user).cleanUpPhone().user;
/**
 * Evaluate an object of type Partial<EncryptedUser> with all
 * required properties to resolve a non-partial type
 */

function getValidatedUser(user) {
  const cleanedUser = cleanUpUser(user);

  if (!isUserValid(cleanedUser)) {
    throw Error(`User does not pass validation:
      ${getKeyValidationErrors(cleanedUser, requiredUserKeys)}`);
  }

  return cleanedUser;
}
const getValidatedUserWithoutID = user => {
  const {
    info,
    role,
    status,
    passwordCreated,
    creationTimestamp
  } = user;

  if (role === undefined || creationTimestamp === undefined || info === undefined || status !== UserStatus.INCOMPLETE || passwordCreated === undefined) {
    return false;
  }

  const {
    firstName,
    lastName,
    email
  } = info;

  if (firstName === undefined || lastName === undefined || email === undefined) {
    return false;
  }

  const validatedUser = Object.assign(Object.assign({}, user), {
    creationTimestamp,
    info: Object.assign(Object.assign({}, user.info), {
      firstName,
      lastName,
      email
    }),
    status,
    passwordCreated,
    role
  });
  return validatedUser;
};
const getValidatedUserWithID = user => {
  const {
    id,
    info,
    role,
    status,
    passwordCreated,
    creationTimestamp
  } = user;

  if (role === undefined || info === undefined || creationTimestamp === undefined || passwordCreated === undefined || status === undefined) {
    return false;
  }

  const {
    firstName,
    lastName,
    email,
    phone
  } = info;

  if (firstName === undefined || lastName === undefined || id === undefined || email === undefined) {
    return false;
  }

  const validatedUnknownUser = Object.assign(Object.assign({}, user), {
    id,
    creationTimestamp,
    info: Object.assign(Object.assign({}, info), {
      firstName,
      lastName,
      email,
      phone
    }),
    status,
    passwordCreated,
    role
  });
  const validatedUser = Object.assign(Object.assign({}, validatedUnknownUser), {
    info: Object.assign(Object.assign({}, validatedUnknownUser.info), {
      phone
    }),
    status: UserStatus.INCOMPLETE
  });
  return validatedUser;
};

export { cleanUpUser, getPhoneNumberErrors, getUserInfoErrors, getValidatedInsuranceInfo, getValidatedPhoneNumber, getValidatedUser, getValidatedUserInfo, getValidatedUserWithID, getValidatedUserWithoutID, isInsuranceInfoValid, isOrderedMedicalTitle, isPhoneNumber, isUserInfo, isUserValid };
